import React, { useMemo } from 'react';
import clsx from 'clsx';
import { useIntl } from 'react-intl';
import { assetKindOf } from 'fym-shared/src/music24/assetKinds';
import type { RolloutDayKey } from 'fym-shared/src/music24/releaseRollout';
import { TypedMemo } from '../../../../../common/types';
import { getFullAPIFileURL } from '../../../../../common/getFullAPIFileURL';
import { planAssetName } from '../rolloutDayCopy';
import { useRollout } from './RolloutContext';
import type { PlanStepView } from './rolloutModel';
import { sheetMessages } from './sheetMessages';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the step sheet; each part uses its own slice of it */
import styles from './Rollout.module.scss';
import sheetStyles from './StepSheet.module.scss';
/* eslint-enable css-modules/no-unused-class */

/** The most files the swap offers. */
const MAX_OPTIONS = 8;

interface SwapOption {
  fileId: number;
  label: string;
  picture: string | null;
  /** The asset type of the step comes first. */
  sameType: boolean;
}

/**
 * "Asset of this step" (design rollout-plan c1): the file of the step and the other post files of
 * the song of the same kind (an image step takes an image, a video step a video), the type of the
 * step first. A pick changes the file of every open post of the step (`POST .../media`).
 */
const StepSheetAssetComponent: React.FC<{ step: PlanStepView }> = ({ step }) => {
  const intl = useIntl();
  const { release, actions, canAct } = useRollout();
  const isVideo = step.day.format === 'reel';
  const current = step.day.asset?.fileId ?? null;
  const themeId = step.day.asset?.themeId ?? '';

  const options = useMemo<SwapOption[]>(() => {
    const kit = (release.ready ?? release.assets)
      .filter((asset) => asset.status === 'ready' && asset.fileId !== null && assetKindOf(asset.themeId) === 'post')
      .filter((asset) => (asset.durationSec !== null) === isVideo)
      .map((asset) => ({
        fileId: asset.fileId as number,
        label: `${planAssetName(intl, asset.themeId)} r${asset.version}`,
        picture: asset.posterUrl ?? (isVideo ? null : asset.fileUrl),
        sameType: asset.themeId === themeId,
      }));
    const reels = isVideo
      ? release.reels
          .filter((reel) => (reel.status === 'ready' || reel.status === 'approved') && reel.video !== null)
          .map((reel) => ({
            fileId: reel.video?.fileId as number,
            label: `${planAssetName(intl, 'aiReel')} r${reel.revision ?? 1}`,
            picture: reel.video?.posterUrl ?? null,
            sameType: themeId === 'aiReel',
          }))
      : [];
    return [...kit, ...reels].sort((a, b) => Number(b.sameType) - Number(a.sameType)).slice(0, MAX_OPTIONS);
  }, [intl, isVideo, release, themeId]);

  if (options.length === 0) {
    return null;
  }
  return (
    <div className={sheetStyles.field}>
      <span className={sheetStyles.label}>{intl.formatMessage(sheetMessages.assetTitle)}</span>
      <div className={sheetStyles.swap}>
        {options.map((option) => (
          <button
            key={option.fileId}
            type="button"
            className={clsx(sheetStyles.swapOption, option.fileId === current && sheetStyles.swapOn)}
            disabled={!canAct || actions.busy !== null || option.fileId === current}
            aria-pressed={option.fileId === current}
            onClick={() => {
              actions.swap('sheet', step.key as RolloutDayKey, option.fileId).catch(console.error);
            }}
          >
            {option.picture === null ? <span /> : <img src={getFullAPIFileURL(option.picture)} alt="" />}
            {option.label}
          </button>
        ))}
      </div>
      <span className={styles.line}>{intl.formatMessage(sheetMessages.swapNote)}</span>
    </div>
  );
};

export const StepSheetAsset = TypedMemo(StepSheetAssetComponent);
